import { Injectable, inject } from '@angular/core';
import { AppConfig } from 'client/app/app.config';
import { ApiClientConstant } from '@cureskin/api-client';
import {
  AiDetectionResult,
  SCALP_SEGMENT_CLASS,
  ScalpCardCopy,
  ScalpCardState,
  ScalpSeverity,
  ScalpSeverityLanguageCopy,
  ScalpSeverityValue,
} from './concern-detections.constants';
import { scalpSeverityCopy } from '../../static-translations/scalp-severity-copy';

const SCALP_LABEL_SEVERITIES: string[] = [ScalpSeverity.Mild, ScalpSeverity.Moderate, ScalpSeverity.Severe];

@Injectable({
  providedIn: 'root',
})
export class ConcernDetectionsService {
  private readonly appConfig: AppConfig = inject(AppConfig);

  getScalpSegmentDetections(results: AiDetectionResult[]): AiDetectionResult[] {
    return (results || []).filter((result: AiDetectionResult): boolean =>
      result.Condition === this.appConfig.Shared.InstantCheckup.DetectionStatus.DETECTED
      && result.class === SCALP_SEGMENT_CLASS);
  }

  getNormalizedScalpSeverity(detection?: AiDetectionResult): string | undefined {
    const rawSeverity = detection?.severity;
    return rawSeverity
      ? rawSeverity.charAt(0).toUpperCase() + rawSeverity.slice(1).toLowerCase()
      : undefined;
  }

  // Finds a Normal-severity scalp-segment regardless of `Condition`. Normal results can arrive
  // with `Condition: 'Not Detected'`, so they are deliberately not filtered by the DETECTED-only
  // `getScalpSegmentDetections()`.
  getNormalScalpDetection(results: AiDetectionResult[]): AiDetectionResult | undefined {
    return (results || []).find((result: AiDetectionResult): boolean =>
      result.class === SCALP_SEGMENT_CLASS && this.getNormalizedScalpSeverity(result) === ScalpSeverity.Normal);
  }

  // Detections render based on drawable masks alone; severity is optional and only drives the
  // label (the prod API may omit it), so it must not gate whether detections are shown.
  hasDrawableScalpMasks(results: AiDetectionResult[]): boolean {
    return this.getScalpSegmentDetections(results).some((detection: AiDetectionResult): boolean => {
      try {
        if (!detection?.scalp_masks) return false;
        const scalpMasks = JSON.parse(detection.scalp_masks || '[]');
        return Array.isArray(scalpMasks) && scalpMasks.length > 0;
      } catch (error) {
        return false;
      }
    });
  }

  getScalpCardState(params: {
    isHairTopView: boolean,
    results: AiDetectionResult[],
    showNoDetectionCard: boolean,
  }): ScalpCardState {
    if (!params.isHairTopView) return ScalpCardState.None;
    if (this.getNormalScalpDetection(params.results)) return ScalpCardState.Normal;
    if (this.hasDrawableScalpMasks(params.results)) return ScalpCardState.Detected;
    if (params.showNoDetectionCard) return ScalpCardState.NoDetection;
    return ScalpCardState.None;
  }

  isScalpLabelSeverity(severity?: string): boolean {
    return !!severity && SCALP_LABEL_SEVERITIES.includes(severity);
  }

  getScalpCardCopy(params: {
    state: ScalpCardState,
    severity?: string,
    gender?: string,
    language?: string,
  }): ScalpCardCopy | undefined {
    const copy: ScalpSeverityLanguageCopy = scalpSeverityCopy[params.language] || scalpSeverityCopy.en;
    switch (params.state) {
      case ScalpCardState.NoDetection:
        return {
          title: copy.noDetection.title,
          reasonsHeading: copy.noDetection.reasonsHeading,
          reasons: copy.noDetection.reasons,
        };
      case ScalpCardState.Normal:
        return {
          title: copy.titles[ScalpSeverity.Normal],
          description: this.getScalpDescriptions(copy, params.gender)[ScalpSeverity.Normal],
        };
      case ScalpCardState.Detected: {
        const severity = this.toCopySeverity(params.severity);
        return {
          title: copy.titles[severity],
          description: this.getScalpDescriptions(copy, params.gender)[severity],
        };
      }
      default:
        return undefined;
    }
  }

  private getScalpDescriptions(
    copy: ScalpSeverityLanguageCopy,
    gender?: string,
  ): Record<ScalpSeverityValue, string> {
    return gender === ApiClientConstant.User.Gender.FEMALE
      ? copy.descriptions.Female
      : copy.descriptions.Male;
  }

  private toCopySeverity(severity?: string): ScalpSeverityValue {
    return this.isScalpLabelSeverity(severity)
      ? severity as ScalpSeverityValue
      : ScalpSeverity.Unknown;
  }
}
